import React, { useEffect, useMemo, useState } from "react";
import client from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Input, Select, Card, Modal, Badge } from "../components/ui";
import { downloadManual } from "../lib/manuals";
import { translate as t, tp, APP_VERSIONS } from "../i18n";
import { Plus, Download, Search, Trash2, FileSpreadsheet, HelpCircle, Settings2, CalendarClock, Pencil, CheckSquare, Square, X, ChevronRight, ChevronDown, LayoutList, Lock } from "lucide-react";
import { LiveBackupIndicator } from "../components/LiveBackup";
import QuickTour, { replayTour } from "../components/QuickTour";
import { useOptionLists, OptionSetSelect, AccountSelect, ScheduleField } from "../components/CustomFields";
import { PoolAlertFlasher } from "../components/PoolAlertFlasher";
import { ScheduledActivity } from "../components/ScheduledActivity";

const COLL_TO_APP = { persons: "family_tree", benefits: "benefits", currencies: "currency", holdings: "holdings" };


// Generic, config-driven CRUD page used by Benefits / Currency / Holdings.
export default function ResourcePage({ collection, titleKey, titleText, manualKey, fields, columns, groupKey, groupConfig = null, computeStats, statsRender = null, max, rowActions = [], topSlot = null, bottomSlot = null, scheduledApp = null, accent = null, legend = null, quickAdd = null, onCreated = null }) {
  const { lang } = useApp();
  const app = COLL_TO_APP[collection];
  const { lists, saveList } = useOptionLists(app);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState({});
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null); // { action, row }
  const [q, setQ] = useState("");
  const [dynOpts, setDynOpts] = useState({});
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [expandedCats, setExpandedCats] = useState(() => new Set());
  const [fullMapCats, setFullMapCats] = useState(() => new Set());
  const blank = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""])), [fields]);
  const [form, setForm] = useState(blank);
  const [qaBusy, setQaBusy] = useState(false);

  const load = () => client.get(`/${collection}`).then((r) => setRows(r.data.map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
  useEffect(() => { load(); }, [collection]);

  useEffect(() => {
    const cols = [...new Set(fields.filter((f) => f.dynamicCollection).map((f) => f.dynamicCollection))];
    cols.forEach((c) => client.get(`/${c}`).then((r) =>
      setDynOpts((o) => ({ ...o, [c]: r.data.map((x) => ({ id: x.id, label: x.name || x.id })) }))).catch(() => {}));
  }, [fields]);

  const add = async () => {
    if (editId) { await client.put(`/${collection}/${editId}`, { data: form }); }
    else {
      const r = await client.post(`/${collection}`, { data: form });
      const created = { id: r.data?.id || r.data?.data?.id, ...form };
      if (onCreated && created.id) { try { onCreated(created); } catch (e) { /* noop */ } }
    }
    setOpen(false); setEditId(null); setForm(blank); load();
  };
  const del = async (id) => { try { await client.delete(`/${collection}/${id}`); } catch (e) { window.alert(e?.response?.data?.detail || "Could not delete."); } load(); };
  const quickAddMany = async (n) => {
    if (!quickAdd) return;
    setQaBusy(true);
    try {
      for (let i = 0; i < n; i++) await client.post(`/${collection}`, { data: { ...(quickAdd.template || {}) } });
      await load();
    } catch (e) { /* noop */ } finally { setQaBusy(false); }
  };
  const openAdd = () => { setEditId(null); setForm(blank); setOpen(true); };
  const openEdit = (row) => {
    const vals = {};
    fields.forEach((f) => { vals[f.key] = row[f.key] ?? (f.default ?? ""); });
    setEditId(row.id); setForm(vals); setOpen(true);
  };
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleCat = (c) => setExpandedCats((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const toggleFullMap = (c) => setFullMapCats((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const clearSel = () => setSelected(new Set());
  const selCount = selected.size;
  const editSelected = () => {
    if (selected.size !== 1) return;
    const row = rows.find((x) => x.id === [...selected][0]);
    if (row) openEdit(row);
  };

  const exportCsv = async () => {
    try {
      const r = await client.get(`/export/${collection}.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${collection}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("exportCsv failed", e); }
  };

  const filtered = rows.filter((r) => !q || columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q.toLowerCase())));
  const stats = computeStats ? computeStats(rows) : [];
  const grouped = groupKey
    ? filtered.reduce((acc, r) => { const g = r[groupKey] || "—"; (acc[g] = acc[g] || []).push(r); return acc; }, {})
    : { All: filtered };

  const labelFor = (f) => (lists.field_labels && lists.field_labels[f.key]) || tp(f.label, lang);
  const openLabels = () => { setLabelDraft({ ...(lists.field_labels || {}) }); setLabelsOpen(true); };
  const saveLabels = async () => { await saveList("field_labels", labelDraft); setLabelsOpen(false); };

  const renderModalField = (f) => {
    const val = form[f.key];
    const set = (e) => setForm({ ...form, [f.key]: e.target.value });
    if (f.type === "optionset")
      return <OptionSetSelect label={labelFor(f)} value={val} onChange={set} presets={f.presets || []} customKey={f.customKey} maxCustom={f.maxCustom || 8} lists={lists} saveList={saveList} testid={`f-${f.key}`} />;
    if (f.type === "account")
      return <AccountSelect label={labelFor(f)} value={val} onChange={set} customKey={f.customKey || "accounts"} lists={lists} saveList={saveList} testid={`f-${f.key}`} />;
    if (f.type === "schedule")
      return <ScheduleField label={labelFor(f)} value={val} onChange={set} testid={`f-${f.key}`} />;
    if (f.dynamicCollection)
      return <Select label={labelFor(f)} value={val} onChange={set} data-testid={`f-${f.key}`}><option value="">{tp("— none —", lang)}</option>{(dynOpts[f.dynamicCollection] || []).map((o) => <option key={o.id} value={o.id}>{tp(o.label, lang)}</option>)}</Select>;
    if (f.type === "dependent") {
      const parentVal = form[f.dependsOn];
      const opts = (f.optionsMap && f.optionsMap[parentVal]) || [];
      if (opts.length)
        return <Select label={labelFor(f)} value={val} onChange={set} data-testid={`f-${f.key}`}><option value="">{tp(f.placeholder || "— select —", lang)}</option>{opts.map((o) => <option key={o} value={o}>{tp(o, lang)}</option>)}</Select>;
      return <Input label={labelFor(f)} type="text" value={val} onChange={set} placeholder={tp(f.freePlaceholder || "Optional", lang)} data-testid={`f-${f.key}`} />;
    }
    if (f.options)
      return <Select label={labelFor(f)} value={val} onChange={set} data-testid={`f-${f.key}`}>{f.allowEmpty && <option value="">{f.placeholder || "— select —"}</option>}{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</Select>;
    return <Input label={labelFor(f)} type={f.type || "text"} maxLength={f.max || undefined} value={val} onChange={set} data-testid={`f-${f.key}`} />;
  };

  const renderTable = (items) => (
    <Card className="overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-black/10 dark:border-white/10 text-start">
          <th className="p-3 w-8"></th>
          {columns.map((c) => <th key={c.key} className="p-3 text-start font-medium opacity-70">{tp(c.label, lang)}</th>)}
          <th className="p-3"></th>
        </tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={columns.length + 2} className="p-4 text-center opacity-50">{tp("No records.", lang)}</td></tr>}
          {items.map((r) => (
            <tr key={r.id} className={`border-b border-black/5 dark:border-white/5 lift hover:bg-black/[0.03] dark:hover:bg-white/[0.03] ${selected.has(r.id) ? "bg-[var(--primary)]/5" : ""}`} data-testid={`row-${r.id}`}>
              <td className="p-3">
                <button onClick={() => toggleSel(r.id)} data-testid={`select-${collection}-${r.id}`} className="opacity-60 hover:opacity-100" aria-label="Select row">
                  {selected.has(r.id) ? <CheckSquare size={17} className="text-[var(--primary)]" /> : <Square size={17} />}
                </button>
              </td>
              {columns.map((c) => <td key={c.key} className="p-3">{c.render ? c.render(r[c.key], r) : (r[c.key] || "—")}</td>)}
              <td className="p-3 text-end whitespace-nowrap">
                <button onClick={() => openEdit(r)} data-testid={`edit-${collection}-${r.id}`} className="me-3 opacity-50 hover:opacity-100 hover:text-[#0F62FE]"><Pencil size={15} /></button>
                {rowActions.map((a) => (
                  <button key={a.testid} onClick={() => (a.onClick ? a.onClick(r) : setActive({ action: a, row: r }))} data-testid={`${a.testid}-${r.id}`}
                    className="me-3 inline-flex items-center gap-1 text-xs opacity-60 hover:opacity-100 hover:text-[#0F62FE]">
                    {a.icon}{tp(a.label, lang)}
                  </button>
                ))}
                {r.permanent
                  ? <span title={tp("Standard record — always present, can't be deleted", lang)} data-testid={`perm-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-medium opacity-60"><Lock size={13} /> {tp("Standard", lang)}</span>
                  : <button onClick={() => del(r.id)} data-testid={`del-${r.id}`} className="opacity-50 hover:opacity-100 hover:text-[#DA1E28]"><Trash2 size={15} /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );

  const renderSubsections = (cat, items, subs) => {
    const showAll = fullMapCats.has(cat);
    const bySub = {};
    items.forEach((it) => { const s = it[groupConfig.subKey] || "— unspecified —"; (bySub[s] = bySub[s] || []).push(it); });
    const definedShown = subs.filter((s) => showAll || (bySub[s] && bySub[s].length));
    const extras = Object.keys(bySub).filter((s) => !subs.includes(s));
    const emptyCount = subs.filter((s) => !(bySub[s] && bySub[s].length)).length;
    return (
      <>
        {emptyCount > 0 && (
          <button type="button" onClick={() => toggleFullMap(cat)} data-testid={`hold-fullmap-${cat}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--primary)] hover:underline">
            <LayoutList size={13} /> {showAll ? tp("Show only populated sub-sections", lang) : `${tp("Show all", lang)} ${subs.length} ${tp("sub-sections", lang)} (${emptyCount} ${tp("empty", lang)})`}
          </button>
        )}
        {definedShown.map((s) => (
          <div key={s} className="space-y-1.5" data-testid={`hold-sub-${cat}-${s}`}>
            <div className="flex items-center gap-2 ps-1 text-xs font-medium opacity-70"><span>{tp(s, lang)}</span><Badge color={(bySub[s] || []).length ? "info" : "neutral"}>{(bySub[s] || []).length}</Badge></div>
            {(groupConfig.subRender && groupConfig.subRender(cat, s, bySub[s] || [], load)) || renderTable(bySub[s] || [])}
          </div>
        ))}
        {extras.map((s) => (
          <div key={s} className="space-y-1.5" data-testid={`hold-sub-${cat}-${s}`}>
            <div className="flex items-center gap-2 ps-1 text-xs font-medium opacity-70"><span>{tp(s, lang)}</span><Badge>{bySub[s].length}</Badge></div>
            {(groupConfig.subRender && groupConfig.subRender(cat, s, bySub[s], load)) || renderTable(bySub[s])}
          </div>
        ))}
      </>
    );
  };

  const renderCategory = (cat, forcedItems) => {
    const items = forcedItems || grouped[cat] || [];
    const isOpen = expandedCats.has(cat);
    const subs = (groupConfig.subsections && groupConfig.subsections[cat]) || [];
    return (
      <div key={cat} className="overflow-hidden rounded-lg border border-black/10 dark:border-white/10" data-testid={`hold-cat-${cat}`}>
        <button type="button" onClick={() => toggleCat(cat)} data-testid={`hold-cat-toggle-${cat}`}
          className="flex w-full items-center gap-2 bg-black/[0.03] px-3 py-2.5 text-start transition-colors hover:bg-black/[0.06] dark:bg-white/[0.04] dark:hover:bg-white/[0.07]">
          {isOpen ? <ChevronDown size={16} className="opacity-70" /> : <ChevronRight size={16} className="opacity-70" />}
          <span className="font-head text-sm" style={accent ? { color: accent } : undefined}>{tp(cat, lang)}</span>
          <Badge color={items.length ? "info" : "neutral"}>{items.length}</Badge>
          {subs.length > 0 && <span className="text-[11px] opacity-50">· {subs.length} {tp("sub-sections", lang)}</span>}
        </button>
        {isOpen && (
          <div className="space-y-3 p-3">
            {groupConfig.categoryRender && groupConfig.categoryRender(cat, items, load)}
            {(() => {
              const custom = groupConfig.categoryBody && groupConfig.categoryBody(cat, items, load);
              if (custom) return custom;
              return subs.length > 0 ? renderSubsections(cat, items, subs) : renderTable(items);
            })()}
          </div>
        )}
      </div>
    );
  };

  const knownCats = new Set(groupConfig ? groupConfig.categories : []);
  const uncategorized = groupConfig ? filtered.filter((r) => !knownCats.has(r[groupConfig.categoryKey])) : [];

  return (
    <div className="space-y-6 fadeup">
      <QuickTour
        appKey={COLL_TO_APP[collection] || collection}
        targetTestId={`add-${collection}`}
        title={tp("Welcome — start here", lang)}
        lines={[
          tp("Click the blue ＋ Add button (top-right) to add a new entry and fill in its details.", lang),
          tp("Use CSV to export a spreadsheet, and Manual for the full step-by-step guide.", lang),
        ]}
      />
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl" style={accent ? { borderInlineStart: `5px solid ${accent}`, paddingInlineStart: "12px" } : undefined}>{titleText || t(titleKey, lang)}</h1>
        {APP_VERSIONS[COLL_TO_APP[collection]] && <Badge color="success" data-testid={`${collection}-version`}>v{APP_VERSIONS[COLL_TO_APP[collection]]}</Badge>}
        {max && <Badge color={rows.length >= max ? "danger" : "info"}>{rows.length}/{max}</Badge>}
        {COLL_TO_APP[collection] && <LiveBackupIndicator app={COLL_TO_APP[collection]} />}
        <div className="ms-auto flex gap-2">
          <Button variant="ghost" onClick={() => replayTour(COLL_TO_APP[collection] || collection)} title="Replay quick tour" data-testid={`${collection}-tour`}><HelpCircle size={16} /></Button>
          {scheduledApp && <Button variant="ghost" onClick={() => setSchedOpen(true)} title="Scheduled activity" data-testid={`${collection}-scheduled`}><CalendarClock size={16} /> {tp("Scheduled", lang)}</Button>}
          <Button variant="ghost" onClick={openLabels} title="Rename fields" data-testid={`${collection}-labels`}><Settings2 size={16} /> {tp("Fields", lang)}</Button>
          <Button variant="ghost" onClick={() => downloadManual(manualKey)} data-testid={`${collection}-manual`}><Download size={16} /> {tp("Manual", lang)}</Button>
          <Button variant="ghost" onClick={exportCsv} data-testid={`${collection}-csv`}><FileSpreadsheet size={16} /> CSV</Button>
          <Button onClick={openAdd} disabled={max && rows.length >= max} style={accent ? { background: accent, borderColor: accent } : undefined} data-testid={`add-${collection}`}><Plus size={16} /> {t("add", lang)}</Button>
        </div>
      </div>

      {topSlot}

      <PoolAlertFlasher />

      {statsRender ? statsRender(rows, load) : (stats.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <Card key={s.label} className="space-y-1"><div className="text-xs opacity-60">{tp(s.label, lang)}</div><div className="font-head text-2xl">{s.value}</div></Card>
          ))}
        </div>
      ))}

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${t("search", lang)}...`}
          className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 py-2 ps-9 pe-3 text-sm" data-testid={`${collection}-search`} />
      </div>

      {selCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.04] p-2" data-testid={`${collection}-selbar`}>
          <span className="text-sm opacity-70" data-testid={`${collection}-selcount`}>{selCount} {tp("selected", lang)}</span>
          <Button variant="ghost" disabled={selCount !== 1} onClick={editSelected} title={selCount > 1 ? "Select a single entry to edit" : "Edit selected entry"} data-testid={`${collection}-sel-edit`}><Pencil size={15} /> {tp("Edit", lang)}</Button>
          <Button variant="ghost" onClick={clearSel} data-testid={`${collection}-sel-clear`}><X size={15} /> {tp("Clear", lang)}</Button>
        </div>
      )}

      {legend && (
        <div className="text-xs italic opacity-60" data-testid={`${collection}-legend`}>{tp(legend, lang)}</div>
      )}

      {quickAdd && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--primary)]/40 bg-[var(--primary)]/10 p-2.5" data-testid={`${collection}-quickadd`}>
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--primary)]">{tp(quickAdd.label || "Add more entries", lang)}</span>
          <div className="flex overflow-hidden rounded-md border border-[var(--primary)]/40">
            {(quickAdd.counts || [1, 5, 10]).map((n) => (
              <button key={n} onClick={() => quickAddMany(n)} disabled={qaBusy} data-testid={`${collection}-quickadd-${n}`}
                className="border-e border-[var(--primary)]/30 bg-white/60 px-3 py-1 text-sm font-semibold text-[var(--primary)] last:border-e-0 hover:bg-[var(--primary)] hover:text-white disabled:opacity-40 dark:bg-black/20">
                +{n}
              </button>
            ))}
          </div>
          {qaBusy && <span className="text-xs opacity-60" data-testid={`${collection}-quickadd-busy`}>{tp("Adding…", lang)}</span>}
        </div>
      )}

      {groupConfig ? (
        <div className="space-y-4" data-testid="holdings-grouped">
          {groupConfig.groups.map((grp, gi) => (
            <div key={gi} className="space-y-3">
              {gi > 0 && (
                <div className="my-1 flex items-center gap-2" data-testid="holdings-group-divider">
                  <div className="h-px flex-1 bg-black/15 dark:bg-white/15" />
                  <span className="text-[10px] uppercase tracking-widest opacity-40">•</span>
                  <div className="h-px flex-1 bg-black/15 dark:bg-white/15" />
                </div>
              )}
              {grp.categories.map((cat) => renderCategory(cat))}
            </div>
          ))}
          {uncategorized.length > 0 && renderCategory(tp("Uncategorized", lang), uncategorized)}
        </div>
      ) : (
        Object.entries(grouped).map(([g, items]) => (
          <div key={g} className="space-y-2">
            {groupKey && <div className="font-head text-sm uppercase tracking-wide" style={accent ? { color: accent } : undefined}>{!accent && <span className="opacity-60">{tp(g, lang)}</span>}{accent && tp(g, lang)}</div>}
            {renderTable(items)}
          </div>
        ))
      )}

      {bottomSlot}

      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={`${editId ? tp("Edit", lang) : t("add", lang)} — ${titleText || t(titleKey, lang)}`} testid={`${collection}-modal`} draggable>
        <div className="grid grid-cols-2 gap-3">
          {fields.map((f) => (
            <div key={f.key} className={["schedule", "account"].includes(f.type) ? "col-span-2" : ""}>{renderModalField(f)}</div>
          ))}
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => { setOpen(false); setEditId(null); }}>{t("cancel", lang)}</Button><Button onClick={add} data-testid={`save-${collection}`}>{t("save", lang)}</Button></div>
        </div>
      </Modal>

      <Modal open={labelsOpen} onClose={() => setLabelsOpen(false)} title={tp("Rename fields", lang)} testid={`${collection}-labels-modal`}>
        <div className="space-y-3">
          <p className="text-xs opacity-60">{tp("Give any field your own name. Leave blank to use the default. Your names apply only to this application.", lang)}</p>
          <div className="grid grid-cols-1 gap-2">
            {fields.map((f) => (
              <Input key={f.key} label={tp(f.label, lang)} value={labelDraft[f.key] || ""} placeholder={tp(f.label, lang)}
                onChange={(e) => setLabelDraft({ ...labelDraft, [f.key]: e.target.value })} data-testid={`label-${f.key}`} />
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1"><Button variant="ghost" onClick={() => setLabelsOpen(false)}>{t("cancel", lang)}</Button><Button onClick={saveLabels} data-testid={`${collection}-labels-save`}>{t("save", lang)}</Button></div>
        </div>
      </Modal>

      {active && (
        <Modal open={!!active} onClose={() => setActive(null)}
          wide={active.action.wide}
          title={active.action.title ? active.action.title(active.row) : active.action.label}
          testid={`${active.action.testid}-modal`}>
          {active.action.render(active.row, () => { setActive(null); load(); })}
        </Modal>
      )}
      {scheduledApp && <ScheduledActivity app={scheduledApp} open={schedOpen} onClose={() => setSchedOpen(false)} />}
    </div>
  );
}
